import { memo, useCallback, useMemo } from 'react'
import { Clock, Music4, TriangleAlert } from 'lucide-react'
import type { AntennaElementType, Program } from '../../types/index.js'
import { antennaElementTypeLabel, formatDuration, formatDurationShort } from '../../lib/utils.js'
import { Spinner } from '../common/Spinner.js'

interface JinglesOverviewProps {
  jingles:      Program[]
  /** Sert à afficher aussi les types encore vides, comme le faisait l'enum. */
  elementTypes: AntennaElementType[]
  loading:      boolean
  onSelect:     (program: Program) => void
}

function mediaOf(program: Program) {
  return program.mediaAssets ?? []
}

/** Un élément d'antenne sans fichier ou dont le fichier a échoué au dernier contrôle. */
function isAtRisk(program: Program): boolean {
  const media = mediaOf(program)
  return media.length === 0 || media.some(asset => asset.status !== 'OK')
}

/**
 * Écran Jingles sans sélection : répartition par type, éléments à contrôler et
 * catalogue. Même principe que Programmes — le centre reste utile tant que
 * l'opérateur n'a rien ouvert.
 */
export const JinglesOverview = memo(function JinglesOverview({
  jingles, elementTypes, loading, onSelect,
}: JinglesOverviewProps) {
  const totalDurationSec = useMemo(
    () => jingles.reduce((total, jingle) => total + (jingle.durationSec ?? 0), 0),
    [jingles],
  )
  const atRisk = useMemo(() => jingles.filter(isAtRisk), [jingles])
  const byType = useMemo(() => {
    const counts = new Map<string, number>()
    for (const elementType of elementTypes) counts.set(elementType.label, 0)
    for (const jingle of jingles) {
      const label = antennaElementTypeLabel(jingle)
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
    return [...counts.entries()]
  }, [elementTypes, jingles])

  return (
    <div className="programs-overview jingles-overview">
      <div className="programs-overview__counters">
        <div className="programs-counter">
          <div className="programs-counter__label"><Music4 size={15} aria-hidden="true" />Éléments</div>
          <div className="programs-counter__value">{jingles.length}</div>
          <div className="programs-counter__detail">tous types d’élément d’antenne</div>
        </div>
        <div className="programs-counter">
          <div className="programs-counter__label"><Clock size={15} aria-hidden="true" />Durée cumulée</div>
          <div className="programs-counter__value">{formatDuration(totalDurationSec)}</div>
          <div className="programs-counter__detail">du catalogue</div>
        </div>
        <div className={`programs-counter${atRisk.length > 0 ? ' is-accent' : ''}`}>
          <div className="programs-counter__label"><TriangleAlert size={15} aria-hidden="true" />À contrôler</div>
          <div className="programs-counter__value">{atRisk.length}</div>
          <div className="programs-counter__detail">
            {atRisk.length > 0 ? 'sans média ou en erreur' : 'catalogue sain'}
          </div>
        </div>
      </div>

      <section aria-label="Répartition par type">
        <h2 className="programs-overview__heading">Répartition par type</h2>
        <div className="jingles-overview__types">
          {byType.map(([label, count]) => (
            <div key={label} className={`jingles-overview__type${count === 0 ? ' is-empty' : ''}`}>
              <span className="jingles-overview__type-count">{count}</span>
              <span className="jingles-overview__type-label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {atRisk.length > 0 && (
        <section aria-label="Éléments à contrôler">
          <h2 className="programs-overview__heading">À contrôler</h2>
          <div className="jingles-overview__risk">
            {atRisk.slice(0, 8).map(jingle => (
              <RiskRow key={jingle.id} program={jingle} onSelect={onSelect} />
            ))}
            {atRisk.length > 8 && (
              <p className="programs-work-card__more">+ {atRisk.length - 8} autre(s)</p>
            )}
          </div>
        </section>
      )}

      <section aria-label="Catalogue">
        <h2 className="programs-overview__heading">
          Catalogue
          {jingles.length > 0 && <span className="programs-overview__count">{jingles.length}</span>}
        </h2>
        {jingles.length === 0 ? (
          <p className="programs-overview__empty">
            {loading ? <><Spinner /> Chargement du catalogue…</> : 'Aucun élément d’antenne pour cette chaîne.'}
          </p>
        ) : (
          <div className="jingles-overview__grid">
            {jingles.map(jingle => (
              <JingleTile key={jingle.id} program={jingle} onSelect={onSelect} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
})

const RiskRow = memo(function RiskRow({
  program, onSelect,
}: {
  program:  Program
  onSelect: (program: Program) => void
}) {
  const handleClick = useCallback(() => onSelect(program), [onSelect, program])
  const media = mediaOf(program)
  const reason = media.length === 0
    ? 'Aucun média associé'
    : media.find(asset => asset.status !== 'OK')?.status === 'MISSING'
      ? 'Fichier absent du disque'
      : 'Média à re-contrôler'

  return (
    <button type="button" className="jingles-overview__risk-row" onClick={handleClick} title={`Ouvrir ${program.title}`}>
      <span className="jingles-overview__risk-title">{program.title}</span>
      <span className="jingles-overview__risk-type">{antennaElementTypeLabel(program)}</span>
      <span className="jingles-overview__risk-reason">{reason}</span>
    </button>
  )
})

const JingleTile = memo(function JingleTile({
  program, onSelect,
}: {
  program:  Program
  onSelect: (program: Program) => void
}) {
  const handleClick = useCallback(() => onSelect(program), [onSelect, program])
  const risky = isAtRisk(program)

  return (
    <button
      type="button"
      className={`jingles-overview__tile${risky ? ' is-risky' : ''}`}
      onClick={handleClick}
      title={program.title}
    >
      <span className="jingles-overview__tile-title">{program.title}</span>
      <span className="jingles-overview__tile-meta">
        {antennaElementTypeLabel(program)} · {formatDurationShort(program.durationSec)}
      </span>
    </button>
  )
})
